/**
 * KeyboardHelpOverlay: shows the "?" cheatsheet for Go-to shortcuts.
 * Rendered by each layout when helpOpen === true.
 */

import React from 'react';
import { X } from 'lucide-react';
import type { GotoShortcut } from '@/hooks/use-keyboard-nav';

interface KeyboardHelpOverlayProps {
  shortcuts: GotoShortcut[];
  onClose: () => void;
  /** 'lib' uses librarian CSS vars; 'admin' uses plain tailwind */
  variant?: 'lib' | 'admin';
}

const KeyboardHelpOverlay: React.FC<KeyboardHelpOverlayProps> = ({
  shortcuts,
  onClose,
  variant = 'lib',
}) => {
  const isLib = variant === 'lib';

  const panelCls = isLib
    ? 'bg-[var(--lib-surface)] border border-[var(--lib-hairline)] text-[var(--lib-ink)]'
    : 'bg-white border border-gray-200 text-gray-900';

  const kbdCls = isLib
    ? 'bg-[var(--lib-hairline)] text-[var(--lib-ink-muted)]'
    : 'bg-gray-100 text-gray-500';

  const headerCls = isLib
    ? 'border-b border-[var(--lib-hairline)] text-[var(--lib-ink)]'
    : 'border-b border-gray-100 text-gray-900';

  const mutedCls = isLib ? 'text-[var(--lib-ink-muted)]' : 'text-gray-500';

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-start justify-center pt-[18vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className={`relative w-full max-w-[420px] rounded-xl shadow-2xl overflow-hidden ${panelCls}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-3.5 ${headerCls}`}>
          <span className="text-[14px] font-semibold">Keyboard shortcuts</span>
          <button
            onClick={onClose}
            className={`rounded-md p-1 transition-colors ${isLib ? 'hover:bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)]' : 'hover:bg-gray-100 text-gray-400'}`}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation shortcuts */}
        <div className="px-5 py-4">
          <p className={`text-[11px] font-mono uppercase tracking-wider mb-3 ${mutedCls}`}>Go to…</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {shortcuts.map((s) => (
              <div key={s.key} className="flex items-center justify-between">
                <span className={`text-[13px] ${mutedCls}`}>{s.label}</span>
                <div className="flex items-center gap-1">
                  <kbd className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${kbdCls}`}>G</kbd>
                  <kbd className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${kbdCls}`}>{s.key}</kbd>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Global shortcuts */}
        <div className={`px-5 pb-4 border-t ${isLib ? 'border-[var(--lib-hairline)]' : 'border-gray-100'} pt-4`}>
          <p className={`text-[11px] font-mono uppercase tracking-wider mb-3 ${mutedCls}`}>General</p>
          <div className="flex flex-col gap-2">
            {[
              { hint: '⌘K · Ctrl+K', label: 'Open command palette' },
              { hint: '?', label: 'Toggle this help' },
              { hint: 'Esc', label: 'Close overlay / dialog' },
            ].map((row) => (
              <div key={row.hint} className="flex items-center justify-between">
                <span className={`text-[13px] ${mutedCls}`}>{row.label}</span>
                <kbd className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${kbdCls}`}>{row.hint}</kbd>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardHelpOverlay;
